import React, { useContext } from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Descriptions,
  Row,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';

import LogSyncDetailsContext from '@totem/components/administration/logSync/details/LogSyncDetailsContext';
import LogSyncStepsTable from '@totem/components/administration/logSync/details/LogSyncStepsTable';
import {
  isConnectionItem,
  LogSyncResults,
} from '@totem/components/administration/logSync/details/types';
import { formatElapsedSeconds } from '@totem/utilities/timeUtilities';

const { Title } = Typography;

const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

export const LogSyncDetailsExtra = () => {
  const { onRefresh } = useContext(LogSyncDetailsContext);

  return (
    <Space>
      <Tooltip title="Refresh">
        <Button icon={<ReloadOutlined />} onClick={onRefresh} />
      </Tooltip>
    </Space>
  );
};

const renderResults = (results: LogSyncResults) => (
  <>
    <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
      <Descriptions.Item label="Success">
        <Tag color={results.success ? 'success' : 'error'}>
          {results.success ? 'Yes' : 'No'}
        </Tag>
      </Descriptions.Item>
      <Descriptions.Item label="Status Code">
        {results.statusCode}
      </Descriptions.Item>
      {!!results.error && (
        <Descriptions.Item label="Error">{results.error}</Descriptions.Item>
      )}
    </Descriptions>
    <Title level={5}>Steps</Title>
    <LogSyncStepsTable steps={results.steps ?? []} />
  </>
);

const LogSyncDetailsLayout = () => {
  const { loading, detail } = useContext(LogSyncDetailsContext);

  if (loading && !detail) {
    return <Skeleton active paragraph={{ rows: 4 }} />;
  }

  if (!detail) {
    return null;
  }

  const { item } = detail;
  const connectionItem = isConnectionItem(item) ? item : null;

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
        <Col xs={24} md={12}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Name">{detail.name}</Descriptions.Item>
            <Descriptions.Item label="Created At">
              {dayjs(detail.createdAt).format(DATE_TIME_FORMAT)}
            </Descriptions.Item>
            {item.startedAt && (
              <Descriptions.Item label="Started">
                {dayjs(item.startedAt).format(DATE_TIME_FORMAT)}
              </Descriptions.Item>
            )}
            {item.endedAt && (
              <Descriptions.Item label="Ended">
                {dayjs(item.endedAt).format(DATE_TIME_FORMAT)}
              </Descriptions.Item>
            )}
            {item.durationSeconds !== undefined && (
              <Descriptions.Item label="Duration">
                {formatElapsedSeconds(item.durationSeconds)}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Col>
        {connectionItem && (
          <Col xs={24} md={12}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Connection">
                {connectionItem.connection?.name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Site">
                {connectionItem.site?.name || '—'}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        )}
      </Row>

      {connectionItem ? (
        <>
          {connectionItem.counters &&
            Object.keys(connectionItem.counters).length > 0 && (
              <>
                <Title level={5}>Counters</Title>
                <Descriptions
                  column={{ xs: 1, sm: 2, md: 3 }}
                  size="small"
                  bordered
                  style={{ marginBottom: 16 }}
                >
                  {Object.entries(connectionItem.counters).map(
                    ([key, value]) => (
                      <Descriptions.Item key={key} label={key}>
                        {value}
                      </Descriptions.Item>
                    ),
                  )}
                </Descriptions>
              </>
            )}
          {renderResults(connectionItem.results)}
        </>
      ) : (
        renderResults(item as LogSyncResults)
      )}
    </>
  );
};

export default LogSyncDetailsLayout;
